import {
  add, angleDegrees, centroid, closestPointOnTriangle, distance, dot, edgeKey,
  length, midpoint, normalize, pointSegmentClosest, scale, sub,
} from './geometry';
import type { BoundaryChain, BoundaryGroup, BoundarySegment, SourcePart, Vec3 } from './types';

interface EdgeRecord { partIndex: number; leafId: number; a: Vec3; b: Vec3 }

export function buildBoundaries(parts: SourcePart[]): { chains: BoundaryChain[]; groups: BoundaryGroup[]; segments: BoundarySegment[] } {
  const segments: BoundarySegment[] = [];
  for (let partIndex = 0; partIndex < parts.length; partIndex += 1) {
    const part = parts[partIndex];
    const edges = new Map<string, EdgeRecord[]>();
    for (const leaf of part.leaves) {
      const [a, b, c] = leaf.vertices;
      for (const [p, q] of [[a, b], [b, c], [c, a]] as [Vec3, Vec3][]) {
        const key = edgeKey(p, q);
        const list = edges.get(key);
        const record = { partIndex, leafId: leaf.id, a: p, b: q };
        if (list) list.push(record); else edges.set(key, [record]);
      }
    }
    for (const records of edges.values()) {
      if (records.length !== 2) continue;
      let first = records[0]; let second = records[1];
      let leafA = part.leaves[first.leafId]; let leafB = part.leaves[second.leafId];
      if (leafA.extruder === leafB.extruder) continue;
      if (leafA.extruder > leafB.extruder) {
        [first, second] = [second, first];
        [leafA, leafB] = [leafB, leafA];
      }
      const center = midpoint(first.a, first.b);
      const towardB = sub(centroid(...leafB.vertices), center);
      const normal = normalize(add(leafA.normal, leafB.normal));
      const tangentPlaneB = normalize(sub(towardB, scale(normal, dot(towardB, normal))));
      segments.push({
        a: first.a, b: first.b, partIndex,
        leafA: leafA.id, leafB: leafB.id,
        sourceTriangleA: leafA.sourceTriangle, sourceTriangleB: leafB.sourceTriangle,
        colorA: leafA.extruder, colorB: leafB.extruder,
        normal, lateralToB: tangentPlaneB,
      });
    }
  }
  const chains = chainSegments(segments);
  return { chains, groups: groupContinuousChains(chains, parts), segments };
}

/** Collapse triangle and T-junction fragments into continuous colour interfaces. */
export function groupContinuousChains(chains: BoundaryChain[], parts?: SourcePart[]): BoundaryGroup[] {
  const parent = chains.map((_, index) => index);
  const find = (input: number): number => {
    let value = input;
    while (parent[value] !== value) value = parent[value];
    const root = value;
    value = input;
    while (parent[value] !== value) { const next = parent[value]; parent[value] = root; value = next; }
    return root;
  };
  const union = (a: number, b: number): void => {
    const rootA = find(a); const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };
  const owners = new Map<string, number>();
  const collinear = new Map<string, { chainIndex: number; start: number; end: number }[]>();
  const spatialSegments: { chainIndex: number; a: Vec3; b: Vec3 }[] = [];
  for (let chainIndex = 0; chainIndex < chains.length; chainIndex += 1) {
    const chain = chains[chainIndex];
    const count = chain.closed ? chain.originalPoints.length : chain.originalPoints.length - 1;
    for (let segment = 0; segment < count; segment += 1) {
      const a = chain.originalPoints[segment]; const b = chain.originalPoints[(segment + 1) % chain.originalPoints.length];
      spatialSegments.push({ chainIndex, a, b });
      const delta = sub(b, a);
      const segmentLength = length(delta);
      if (segmentLength > 1e-10) {
        let direction = scale(delta, 1 / segmentLength);
        const leading = Math.abs(direction[0]) > 1e-8 ? direction[0] : Math.abs(direction[1]) > 1e-8 ? direction[1] : direction[2];
        if (leading < 0) direction = scale(direction, -1);
        const moment: Vec3 = [
          a[1] * direction[2] - a[2] * direction[1],
          a[2] * direction[0] - a[0] * direction[2],
          a[0] * direction[1] - a[1] * direction[0],
        ];
        const lineKey = `${chain.partIndex}:${chain.colorA}:${chain.colorB}:${direction.map((value) => Math.round(value * 1e6)).join(',')}:${moment.map((value) => Math.round(value * 1e5)).join(',')}`;
        const pa = dot(a, direction); const pb = dot(b, direction);
        const records = collinear.get(lineKey);
        const record = { chainIndex, start: Math.min(pa, pb), end: Math.max(pa, pb) };
        if (records) records.push(record); else collinear.set(lineKey, [record]);
      }
      // Slicer subdivision neighbours may meet at a dyadic point without
      // sharing a complete edge. Eighth samples reconnect those fragments.
      for (let sample = 0; sample <= 8; sample += 1) {
        const t = sample / 8;
        const point: Vec3 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
        const key = `${chain.partIndex}:${chain.colorA}:${chain.colorB}:${componentPointKey(point)}`;
        const owner = owners.get(key);
        if (owner === undefined) owners.set(key, chainIndex); else union(chainIndex, owner);
      }
    }
  }
  // Join partially overlapping coarse/fine edges even when the shared point is
  // deeper than the sampling grid above.
  for (const records of collinear.values()) {
    records.sort((a, b) => a.start - b.start || b.end - a.end);
    let owner = records[0];
    for (let index = 1; index < records.length; index += 1) {
      const record = records[index];
      if (record.start <= owner.end + 1e-5) {
        union(owner.chainIndex, record.chainIndex);
        if (record.end > owner.end) owner = record;
      } else owner = record;
    }
  }
  joinPointsOnSegments(chains, spatialSegments, union);
  if (parts) joinAcrossSourceEdges(chains, parts, union);
  const grouped = new Map<number, number[]>();
  for (let index = 0; index < chains.length; index += 1) {
    const root = find(index); const list = grouped.get(root);
    if (list) list.push(index); else grouped.set(root, [index]);
  }
  return [...grouped.values()].map((indices, groupIndex) => {
    const first = chains[indices[0]];
    return {
      id: `boundary-group-${groupIndex + 1}`,
      partIndex: first.partIndex,
      colorA: first.colorA,
      colorB: first.colorB,
      chainIds: indices.map((index) => chains[index].id),
    };
  });
}

function joinAcrossSourceEdges(
  chains: BoundaryChain[],
  parts: SourcePart[],
  union: (a: number, b: number) => void,
): void {
  for (let partIndex = 0; partIndex < parts.length; partIndex += 1) {
    const part = parts[partIndex];
    const chainsByTriangle = new Map<number, number[]>();
    for (let chainIndex = 0; chainIndex < chains.length; chainIndex += 1) {
      const chain = chains[chainIndex];
      if (chain.partIndex !== partIndex) continue;
      for (const triangle of chain.sourceTriangles) {
        const list = chainsByTriangle.get(triangle);
        if (list) list.push(chainIndex); else chainsByTriangle.set(triangle, [chainIndex]);
      }
    }
    const sourceEdges = new Map<string, { triangle: number; a: number; b: number }[]>();
    for (let triangle = 0; triangle < part.triangles.length; triangle += 1) {
      const [a, b, c] = part.triangles[triangle].vertices;
      for (const [v0, v1] of [[a, b], [b, c], [c, a]] as [number, number][]) {
        const key = v0 < v1 ? `${v0}:${v1}` : `${v1}:${v0}`;
        const list = sourceEdges.get(key); const record = { triangle, a: v0, b: v1 };
        if (list) list.push(record); else sourceEdges.set(key, [record]);
      }
    }
    for (const records of sourceEdges.values()) {
      if (records.length !== 2) continue;
      const left = records[0]; const right = records[1];
      const leftChains = chainsByTriangle.get(left.triangle) ?? [];
      const rightChains = chainsByTriangle.get(right.triangle) ?? [];
      if (!leftChains.length || !rightChains.length) continue;
      const edgeA = part.vertices[left.a]; const edgeB = part.vertices[left.b];
      for (const leftIndex of leftChains) for (const rightIndex of rightChains) {
        if (leftIndex === rightIndex) continue;
        const a = chains[leftIndex]; const b = chains[rightIndex];
        if (a.colorA !== b.colorA || a.colorB !== b.colorB) continue;
        if (chainTouchesEdge(a, edgeA, edgeB) && chainTouchesEdge(b, edgeA, edgeB)) union(leftIndex, rightIndex);
      }
    }
  }
}

function chainTouchesEdge(chain: BoundaryChain, edgeA: Vec3, edgeB: Vec3): boolean {
  // Paint leaves on opposite source faces may stop slightly short of the
  // shared edge after independent adaptive subdivision. A small edge-relative
  // allowance reconnects the intended interface without bridging remote paint.
  const tolerance = Math.max(1e-5, distance(edgeA, edgeB) * 0.02);
  return chain.originalPoints.some((point) => pointSegmentClosest(point, edgeA, edgeB).distance <= tolerance);
}

function joinPointsOnSegments(
  chains: BoundaryChain[],
  segments: { chainIndex: number; a: Vec3; b: Vec3 }[],
  union: (a: number, b: number) => void,
): void {
  if (!segments.length) return;
  const allPoints = chains.flatMap((chain) => chain.originalPoints);
  const minimum: Vec3 = [Infinity, Infinity, Infinity]; const maximum: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const point of allPoints) for (let axis = 0; axis < 3; axis += 1) {
    minimum[axis] = Math.min(minimum[axis], point[axis]); maximum[axis] = Math.max(maximum[axis], point[axis]);
  }
  const diagonal = distance(minimum, maximum);
  const cellSize = Math.max(0.05, Math.min(1, diagonal / 500));
  const cells = new Map<string, number[]>();
  const cellKey = (point: Vec3): string => point.map((value) => Math.floor(value / cellSize)).join(',');
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]; const count = Math.max(1, Math.ceil(distance(segment.a, segment.b) / (cellSize * 0.45)));
    for (let sample = 0; sample <= count; sample += 1) {
      const t = sample / count;
      const point: Vec3 = [segment.a[0] + (segment.b[0] - segment.a[0]) * t, segment.a[1] + (segment.b[1] - segment.a[1]) * t, segment.a[2] + (segment.b[2] - segment.a[2]) * t];
      const key = cellKey(point); const list = cells.get(key);
      if (list) { if (list[list.length - 1] !== index) list.push(index); } else cells.set(key, [index]);
    }
  }
  for (let chainIndex = 0; chainIndex < chains.length; chainIndex += 1) {
    for (const point of chains[chainIndex].originalPoints) {
      const base = point.map((value) => Math.floor(value / cellSize));
      const candidates = new Set<number>();
      for (let x = -1; x <= 1; x += 1) for (let y = -1; y <= 1; y += 1) for (let z = -1; z <= 1; z += 1) {
        for (const candidate of cells.get(`${base[0] + x},${base[1] + y},${base[2] + z}`) ?? []) candidates.add(candidate);
      }
      for (const candidate of candidates) {
        const segment = segments[candidate];
        const current = chains[chainIndex]; const other = chains[segment.chainIndex];
        const sameInterface = current.partIndex === other.partIndex && current.colorA === other.colorA && current.colorB === other.colorB;
        if (sameInterface && segment.chainIndex !== chainIndex && pointSegmentClosest(point, segment.a, segment.b).distance <= 1e-5) union(chainIndex, segment.chainIndex);
      }
    }
  }
}

function componentPointKey(point: Vec3): string {
  return point.map((value) => Math.round(value * 1e5)).join(',');
}

function chainSegments(segments: BoundarySegment[]): BoundaryChain[] {
  const groups = new Map<string, number[]>();
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const key = `${segment.partIndex}:${segment.colorA}:${segment.colorB}`;
    const list = groups.get(key); if (list) list.push(index); else groups.set(key, [index]);
  }
  const chains: BoundaryChain[] = [];
  for (const [groupKey, indices] of groups) {
    const incident = new Map<string, number[]>();
    const pointByKey = new Map<string, Vec3>();
    for (const index of indices) {
      const segment = segments[index];
      for (const point of [segment.a, segment.b]) {
        const key = point.map((value) => Math.round(value * 1e6)).join(',');
        pointByKey.set(key, point);
        const list = incident.get(key); if (list) list.push(index); else incident.set(key, [index]);
      }
    }
    const unvisited = new Set(indices);
    while (unvisited.size) {
      const seed = unvisited.values().next().value as number;
      const seedSegment = segments[seed];
      const aKey = pointKey(seedSegment.a); const bKey = pointKey(seedSegment.b);
      let currentKey = (incident.get(aKey)?.length ?? 0) !== 2 ? aKey
        : (incident.get(bKey)?.length ?? 0) !== 2 ? bKey : aKey;
      const points: Vec3[] = [pointByKey.get(currentKey)!];
      const chainIndices: number[] = [];
      const sourceTriangles = new Set<number>();
      let safety = 0;
      while (safety++ <= indices.length + 1) {
        const choices = (incident.get(currentKey) || []).filter((index) => unvisited.has(index));
        if (!choices.length) break;
        const nextIndex = choices[0];
        unvisited.delete(nextIndex); chainIndices.push(nextIndex);
        const segment = segments[nextIndex];
        sourceTriangles.add(segment.sourceTriangleA); sourceTriangles.add(segment.sourceTriangleB);
        const nextKey = pointKey(segment.a) === currentKey ? pointKey(segment.b) : pointKey(segment.a);
        points.push(pointByKey.get(nextKey)!);
        currentKey = nextKey;
        if (currentKey === pointKey(points[0])) break;
        if ((incident.get(currentKey)?.length ?? 0) !== 2) break;
      }
      const first = segments[chainIndices[0] ?? seed];
      const closed = points.length > 2 && pointKey(points[0]) === pointKey(points[points.length - 1]);
      if (closed) points.pop();
      chains.push({
        id: `boundary-${chains.length + 1}`,
        partIndex: first.partIndex, colorA: first.colorA, colorB: first.colorB,
        closed, originalPoints: points, smoothedPoints: points.map((point) => [...point] as Vec3),
        segmentIndices: chainIndices, sourceTriangles: [...sourceTriangles], maxMovement: 0,
      });
    }
  }
  return chains;
}

function pointKey(point: Vec3): string { return point.map((value) => Math.round(value * 1e6)).join(','); }

export function smoothChain(
  chain: BoundaryChain,
  segments: BoundarySegment[],
  part: SourcePart,
  strength: number,
  maxMovement: number,
  tolerance: number,
  creaseAngle: number,
): BoundaryChain {
  if (chain.originalPoints.length < 3) return { ...chain };
  const strongCreases = chain.segmentIndices.filter((index) => {
    const segment = segments[index];
    return angleDegrees(part.leaves[segment.leafA].normal, part.leaves[segment.leafB].normal) >= creaseAngle;
  }).length;
  if (strongCreases / Math.max(1, chain.segmentIndices.length) > 0.7) return { ...chain, maxMovement: 0 };

  const spacing = Math.max(0.1, tolerance * 2);
  const original = resamplePolyline(chain.originalPoints, chain.closed, spacing);
  let points = original.map((point) => [...point] as Vec3);
  const candidateTriangles = projectionCandidates(chain, part);
  const iterations = Math.round(2 + 18 * Math.pow(strength / 100, 1.6));
  const lambda = 0.18 + 0.34 * (strength / 100);

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const next = points.map((point) => [...point] as Vec3);
    const start = chain.closed ? 0 : 1;
    const end = chain.closed ? points.length : points.length - 1;
    for (let index = start; index < end; index += 1) {
      const previous = points[(index - 1 + points.length) % points.length];
      const following = points[(index + 1) % points.length];
      let candidate = add(points[index], scale(sub(scale(add(previous, following), 0.5), points[index]), lambda));
      candidate = clampToOriginal(candidate, original, chain.closed, maxMovement);
      candidate = projectToTriangles(candidate, candidateTriangles);
      candidate = clampToOriginal(candidate, original, chain.closed, maxMovement);
      next[index] = candidate;
    }
    points = next;
  }
  let actualMax = 0;
  for (const point of points) actualMax = Math.max(actualMax, distanceToPolyline(point, original, chain.closed).distance);
  return { ...chain, smoothedPoints: points, maxMovement: actualMax };
}

function projectionCandidates(chain: BoundaryChain, part: SourcePart): [Vec3, Vec3, Vec3][] {
  const indices = new Set(chain.sourceTriangles);
  for (const leaf of part.leaves) {
    if (indices.has(leaf.sourceTriangle)) for (const neighbor of leaf.neighbors) indices.add(part.leaves[neighbor].sourceTriangle);
  }
  return [...indices].map((index) => part.triangles[index]?.vertices.map((vertex) => part.vertices[vertex]) as [Vec3, Vec3, Vec3]).filter(Boolean);
}

function projectToTriangles(point: Vec3, triangles: [Vec3, Vec3, Vec3][]): Vec3 {
  let best = point; let bestDistance = Number.POSITIVE_INFINITY;
  for (const triangle of triangles) {
    const candidate = closestPointOnTriangle(point, ...triangle);
    const d = distance(point, candidate);
    if (d < bestDistance) { bestDistance = d; best = candidate; }
  }
  return best;
}

function clampToOriginal(point: Vec3, original: Vec3[], closed: boolean, maxMovement: number): Vec3 {
  const nearest = distanceToPolyline(point, original, closed);
  if (nearest.distance <= maxMovement) return point;
  return add(nearest.point, scale(normalize(sub(point, nearest.point)), maxMovement));
}

export function distanceToPolyline(point: Vec3, points: Vec3[], closed: boolean): { distance: number; point: Vec3; segment: number; t: number } {
  let best = { distance: Number.POSITIVE_INFINITY, point: points[0], segment: 0, t: 0 };
  const count = closed ? points.length : points.length - 1;
  for (let index = 0; index < count; index += 1) {
    const result = pointSegmentClosest(point, points[index], points[(index + 1) % points.length]);
    if (result.distance < best.distance) best = { ...result, segment: index };
  }
  return best;
}

export function resamplePolyline(points: Vec3[], closed: boolean, spacing: number): Vec3[] {
  if (points.length < 2) return points.map((point) => [...point] as Vec3);
  const source = closed ? [...points, points[0]] : points;
  const cumulative = [0];
  for (let index = 1; index < source.length; index += 1) cumulative.push(cumulative[index - 1] + distance(source[index - 1], source[index]));
  const total = cumulative[cumulative.length - 1];
  const count = Math.max(closed ? 3 : 2, Math.ceil(total / spacing));
  const output: Vec3[] = [];
  const outputCount = closed ? count : count + 1;
  for (let sample = 0; sample < outputCount; sample += 1) {
    const target = total * sample / count;
    let segment = 0;
    while (segment + 1 < cumulative.length && cumulative[segment + 1] < target) segment += 1;
    const span = cumulative[segment + 1] - cumulative[segment] || 1;
    const t = (target - cumulative[segment]) / span;
    output.push(add(source[segment], scale(sub(source[segment + 1], source[segment]), t)));
  }
  return output;
}

export function boundaryLength(chain: BoundaryChain): number {
  const points = chain.smoothedPoints;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += length(sub(points[i], points[i - 1]));
  if (chain.closed && points.length > 1) total += distance(points[points.length - 1], points[0]);
  return total;
}
